// Column definitions for the users table, extracted per Contacts/columns.tsx
// and Product/columns.tsx so the container stays about state and chrome.

import { Avatar } from '@/components/ui/avatar';
import { Counter } from '@/components/ui/counter';
import { cn } from '@/lib/utils';
import { ActiveIcon, DeactivateIcon, EditIcon } from '../icons';
import { DataTableColumnHeader, DataTableRowActions, type ColumnDef } from '../DataTable';
import { USER_COLUMN_WIDTHS } from '../DataTable/columnWidths';
import { WRAP_ANYWHERE_CLASS } from '../DataTable/constants';
import { USER_ID_PADDING } from '../../constants';
import { ACCESS_LEVEL_TAG_CLASSES, accessLevelTier } from './user-access-level-config';
import { canEditUser } from './canEditUser';
import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';
import type { User } from '@api/users';

export interface CreateUserColumnsOptions {
  columnLabels: Record<string, string>;
  actionLabels: Record<string, string>;
  roleLabels: Record<string, string>;
  /** users.write gates the whole actions column; the role scope gates each row. */
  currentUser?: CurrentUser;
  onEditUser: (userId: number) => void;
  onStatusToggle: (user: User) => void;
}

export function createUserColumns({
  columnLabels,
  actionLabels,
  roleLabels,
  currentUser,
  onEditUser,
  onStatusToggle,
}: CreateUserColumnsOptions): ColumnDef<User>[] {
  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={columnLabels.name ?? ''} />,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center gap-3">
            <Avatar size="lg" src={user.avatar_url ?? undefined} name={user.name || user.email} />
            <span className={cn('min-w-0 text-sm font-semibold leading-[1.5] text-off-black capitalize', WRAP_ANYWHERE_CLASS)}>
              {user.name || user.email}
            </span>
            <Counter
              value={`#${String(user.id).padStart(USER_ID_PADDING, '0')}`}
              className="ml-auto flex-shrink-0"
            />
          </div>
        );
      },
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title={columnLabels.email ?? ''} />,
      cell: ({ row }) => (
        <span className="block text-sm leading-[1.5] text-off-black whitespace-nowrap truncate">
          {row.original.email}
        </span>
      ),
    },
    {
      accessorKey: 'mobile_number',
      header: columnLabels.mobile_number ?? '',
      cell: ({ row }) => (
        <span className="text-sm leading-[1.5] text-off-black whitespace-nowrap">
          {row.original.mobile_number || '-'}
        </span>
      ),
      enableSorting: false,
      size: USER_COLUMN_WIDTHS.mobile,
    },
    {
      accessorKey: 'role',
      header: columnLabels.role ?? '',
      cell: ({ row }) => {
        const role = row.original.role;
        const tier = accessLevelTier(role);
        return (
          <span className={cn(ACCESS_LEVEL_TAG_CLASSES, tier.bgClassName, tier.textClassName)}>
            {roleLabels[role] || role}
          </span>
        );
      },
      enableSorting: false,
      size: USER_COLUMN_WIDTHS.role,
    },
  ];

  if (can(currentUser, CAPABILITIES.USERS_WRITE)) {
    columns.push({
      id: 'actions',
      cell: ({ row }) => {
        const user = row.original;
        // Both actions write, so a system account gets neither.
        if (!canEditUser(currentUser, user)) return null;
        const isActive = user.status === 'verified';

        return (
          <DataTableRowActions
            actions={[
              {
                label: actionLabels.edit ?? '',
                icon: <EditIcon className="size-5" />,
                onClick: () => onEditUser(user.id),
              },
              {
                label: isActive ? (actionLabels.deactivate ?? '') : (actionLabels.activate ?? ''),
                icon: isActive
                  ? <DeactivateIcon className="size-5 text-chilli-red" />
                  : <ActiveIcon className="w-3.5 h-3.5 text-brand-yellow" />,
                onClick: () => onStatusToggle(user),
                destructive: isActive,
              },
            ]}
          />
        );
      },
      size: USER_COLUMN_WIDTHS.actions,
    });
  }

  return columns;
}
